import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiService from '../services/api';
import { 
  ClientExerciseException, 
  ClientExerciseExceptionCreate, 
  ClientExerciseExceptionUpdate,
  ExerciseExceptionCheck 
} from '../types';
import toast from 'react-hot-toast';

export const useClientExceptions = (clientId: number) => {
  const queryClient = useQueryClient();

  // Получение списка исключений клиента
  const {
    data: exceptions = [],
    isLoading,
    error,
    refetch
  } = useQuery(
    ['client-exceptions', clientId],
    () => apiService.getClientExceptions(clientId),
    { enabled: !!clientId }
  );

  // Создание исключения
  const createExceptionMutation = useMutation(
    (data: ClientExerciseExceptionCreate) => 
      apiService.createClientException(clientId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-exceptions', clientId]);
        toast.success('Исключение добавлено');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка при добавлении исключения');
      }
    }
  );

  // Обновление исключения
  const updateExceptionMutation = useMutation(
    ({ exceptionId, data }: { exceptionId: number; data: ClientExerciseExceptionUpdate }) =>
      apiService.updateClientException(clientId, exceptionId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-exceptions', clientId]);
        toast.success('Исключение обновлено');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка при обновлении исключения');
      }
    }
  );

  // Удаление исключения
  const deleteExceptionMutation = useMutation(
    (exceptionId: number) => 
      apiService.deleteClientException(clientId, exceptionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-exceptions', clientId]);
        toast.success('Исключение удалено');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.detail || 'Ошибка при удалении исключения');
      }
    }
  );

  // Проверка исключения для конкретного упражнения
  const checkExerciseException = useCallback(async (exerciseId: number): Promise<ExerciseExceptionCheck> => {
    try {
      return await apiService.checkExerciseException(clientId, exerciseId);
    } catch (error: any) {
      console.error('Ошибка при проверке исключения:', error);
      return { has_exception: false, exception: null };
    }
  }, [clientId]);

  return {
    // Данные
    exceptions,
    isLoading,
    error,
    
    // Мутации
    createException: createExceptionMutation.mutate,
    updateException: updateExceptionMutation.mutate,
    deleteException: deleteExceptionMutation.mutate,
    
    // Состояния мутаций
    isCreating: createExceptionMutation.isLoading,
    isUpdating: updateExceptionMutation.isLoading,
    isDeleting: deleteExceptionMutation.isLoading,
    
    // Утилиты
    refetch,
    checkExerciseException,
  };
};

// Хук для проверки исключения одного упражнения
export const useExerciseExceptionCheck = (clientId: number, exerciseId: number) => {
  const {
    data: exceptionCheck,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['exercise-exception-check', clientId, exerciseId],
    () => apiService.checkExerciseException(clientId, exerciseId),
    { enabled: !!clientId && !!exerciseId }
  );

  return {
    hasException: exceptionCheck?.has_exception || false,
    exception: exceptionCheck?.exception || null,
    isLoading,
    error,
    refetch,
  };
}; 